import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Badge } from '@nucleus-commerce/ui/components/badge';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import {
  resolveSeoPreview,
  SeoOverrideFields,
  SeoPreviewCard,
  type SeoStoreContext,
} from '@/features/seo';

import type { ProductFormInput } from '../schemas/product-form-schema';

import { pendingFaqSuggestions } from './faq-suggestions';
import { SharedChip } from './product-scope';
import { SearchEnginesCard, type SearchEnginesTab } from './search-engines-card';
import { SummaryFaqFields, type SummaryFaqFieldsProps } from './summary-faq-section';

export interface ProductSeoSectionProps extends SummaryFaqFieldsProps {
  /** Shown in the preview as the store name; a master product has no store of its own. */
  organisationName: string | undefined;
  hasImage: boolean;
  imageAlt: string | null;
}

/**
 * The master product's Search and answer engines card. The live preview applies the backend
 * defaults a fresh store starts with (`{title} | {store}`, indexable); each store's own template
 * and override are previewed on the store product page. A failed save on the handle, the listing,
 * the summary or the FAQ opens the card on that tab.
 */
export function ProductSeoSection({
  organisationName,
  hasImage,
  imageAlt,
  productType,
}: ProductSeoSectionProps) {
  const { t } = useTranslation('products');
  const form = useFormContext<ProductFormInput>();
  const [title, handle, description, summary, status, seo, faq] = form.watch([
    'title',
    'handle',
    'descriptionHtml',
    'summary',
    'status',
    'seo',
    'faq',
  ]);
  const context: SeoStoreContext = {
    name: organisationName ?? '',
    titleTemplate: '{title} | {store}',
    defaultMetaDescription: null,
    indexable: true,
    primaryDomain: null,
  };
  const path = `/products/${handle || ''}`;
  const live = resolveSeoPreview(
    {
      title,
      description: summary || description || null,
      path,
      published: status === 'active',
      override: seo,
      image: hasImage ? { alt: imageAlt } : 'none',
    },
    context,
  );

  const suggested = pendingFaqSuggestions(faq, productType).length;
  const { errors } = form.formState;
  const errorTab: SearchEnginesTab | undefined =
    errors.handle || errors.seo ? 'listing' : errors.summary || errors.faq ? 'answers' : undefined;

  return (
    <SearchEnginesCard
      badge={<SharedChip />}
      meta={[
        live.warnings.length > 0 ? t('searchCard.warnings', { count: live.warnings.length }) : null,
        suggested > 0 ? t('faq.suggestedCount', { count: suggested }) : null,
      ]}
      errorTab={errorTab}
      answersBadge={
        suggested > 0 ? (
          <Badge variant="secondary">{t('faq.suggestedCount', { count: suggested })}</Badge>
        ) : null
      }
      listing={
        <>
          <FormField
            control={form.control}
            name="handle"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.handle')}</FormLabel>
                <FormControl>
                  <Input {...field} className="font-mono text-[13px]" />
                </FormControl>
                <FormDescription>{t('searchCard.handleHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <SeoOverrideFields bare />
        </>
      }
      preview={<SeoPreviewCard live={live} saved={null} path={path} bare />}
      answers={<SummaryFaqFields productType={productType} />}
    />
  );
}
